import TreeObject from './TreeObject';
import PathsMap from './PathsMap';
import fetch from './fetch';

export default class DefaultApi {
    /**
     * Constructor
     * @param {int} splitCount - depth how deep the navigation should be fetched
     * @param {string} rootPath - root path
     * @param {boolean} auth - true, if user is logged in
     * @param {boolean} active - if this is the current navigation
     */
    constructor(splitCount, rootPath, auth, active) {
        this.apiType = 'default';
        this.pathsMap = new PathsMap();
        this.urlSplitCount = splitCount; // TODO: depth?
        // rootPath is new, use it
        this.rootPath = rootPath;
        this.auth = auth;
        this.active = active;
        this.root = this._init_root();
        // this is needed for structureApi (here we cant extract the active object yet)
        this.activeObject = this._extractActiveObject();

        this.searchEnabled = false;
        this.privateNodesPath = [];
        this.privateNodesParent = undefined;
    }

    /**
     * Construct a (bogus) root TreeObject. It will be later populated with real data.
     */
    _init_root() {
        const emptyTree = {};

        return new TreeObject(this, emptyTree);
    }

    /**
     * Extract active object (abstract method, needs to implemented properly)
     */
    _extractActiveObject() {
        // we cannot determine the active object yet, but we need to set something initially, so we use root
        return this.root;
    }

    /**
     * Search all breadcrumb nodes for private nodes or nodes that are excluded from navigation.
     * Create data objects for these nodes and save them in the pathMap.
     */
    #analyzeBreadcrumb() {
        // get breadcrumb navigation node
        const navBreadcrumb = document.getElementById('nav-breadcrumbs');
        // get all breadcrumb nodes
        const breadcrumbs = (navBreadcrumb) ? navBreadcrumb.querySelectorAll('li') : [];
        const privateCrumbs = [];
        const privateCrumbsParentPath = {};
        let currentPath;
        const invisibleCrumbs = [];

        // create a data object with information about this breadcrumb node
        const createCrumbData = (crumb, crumbMustHaveChildren, crumbPath = '') => {
            // get title from data-real-title attribute if it exists otherwise use span's text as title
            const crumbTitle = (typeof crumb.dataset.realTitle !== 'undefined')
                ? crumb.dataset.realTitle : crumb.querySelector('span').textContent;
            const crumbLinkNode = crumb.querySelector('a');
            let crumbId;
            let crumbUrl = '';
            let isPrivate = false;

            // get the link url if its available
            if (crumbLinkNode) {
                crumbUrl = crumbLinkNode.getAttribute('href');
            } else if (crumbPath) {
                crumbUrl = `${window.PORTAL_URL}${crumbPath}`;
                isPrivate = true;
            }
            // the id is the last part of the url or path
            if (crumbPath) {
                crumbId = crumbPath;
            } else {
                crumbId = crumbUrl;
            }
            crumbId = crumbId.split('/').splice(-1)[0];

            // check data-children, because last crumb might also have children
            // because we might be on the default page of a folderish content object
            // all breadcrumbs except the last do have children anyway
            // use undefined to indicate unknown (for the last one)
            const crumbHasChildren = (crumbMustHaveChildren || undefined);

            const crumbData = {
                children: [],
                has_children: crumbHasChildren,
                is_private: isPrivate,
                is_excluded_from_nav: typeof crumb.dataset.excludeFromNav !== 'undefined',
                review_state: crumb.dataset.reviewState || null,
                id: crumbId,
                title: crumbTitle,
                url: crumbUrl,
            };
            return crumbData;
        };

        // check all breadcrumb nodes if there is a private breadcrumb or a breadcrumb that is excluded from navigation
        for (let i = 0; i < breadcrumbs.length; i += 1) {
            // node with data-path attribute is a private node or excluded from navigation (or both)
            const dataPath = breadcrumbs[i].dataset.path;
            if (dataPath) {
                // is it the last breadcrumb?
                const isNotHeel = i + 1 < breadcrumbs.length;
                const privateData = createCrumbData(breadcrumbs[i], isNotHeel, dataPath);

                // any review_state -> auth
                // push child when (auth and not published) or is_private is set or isExcludedFromNav is set
                if ((privateData.review_state && privateData.review_state !== 'published')
                    || privateData.is_private) {
                    // add possible children for private nodes (except last)
                    if (isNotHeel) {
                        const dataPathNextCrumb = breadcrumbs[i + 1].dataset.path;
                        // dataPathNextCrumb might be undefined
                        privateData.children.push(createCrumbData(
                            breadcrumbs[i + 1],
                            i + 2 < breadcrumbs.length,
                            dataPathNextCrumb,
                        ));
                    }
                }
                // unprivate invisible crumbs get put in the list for additional request pieces and marked for reload
                if (!((privateData.review_state && privateData.review_state !== 'published')
                      || privateData.is_private)
                    && privateData.is_excluded_from_nav) {
                    invisibleCrumbs.push(dataPath);
                    privateData.force_reload = true;
                } else {
                    this.pathsMap.setData(dataPath, privateData);
                }
                // only private crumbs get put in the list for later removal from the request pieces
                if (privateData.is_private) {
                    privateCrumbs.push(dataPath);
                }
                // shorten the data path by the last path segment to identify the parent
                privateCrumbsParentPath[dataPath.split('/').slice(0, -1).join('/')] = privateData;
            }

            // get the current path from the last breadcrumb
            if (i === breadcrumbs.length - 1) {
                // the last breadcrumb can only be private if i'm authenticated
                const crumb = breadcrumbs[i].querySelector('a');
                // (but there are cases without href anyway)
                if (crumb) {
                    const href = crumb.getAttribute('href');
                    currentPath = href.replace(window.PORTAL_URL, '');
                }
            }
        }

        return [privateCrumbs, privateCrumbsParentPath, currentPath, invisibleCrumbs];
    }

    // private method to fetch data from the backend
    #getSubtreeData(path, dynamic = false) {
        return new Promise((resolve, reject) => {
            // check for ? and @@ and remove them
            const pathFixed = path.split('?')[0].split('@@')[0];
            const pathWithSlash = !pathFixed.endsWith('/') ? (`${pathFixed}/`) : pathFixed;
            const url = new URL(`${window.PORTAL_URL}${pathWithSlash}@@subtree_navigation`);
            const params = { depth: this.urlSplitCount };
            // only add auth param when auth is set
            if (this.auth) {
                params.auth = this.auth;
            }
            // add location when dynamic is set
            if (dynamic) {
                params.current_url = window.location;
            }
            url.search = new URLSearchParams(params);
            fetch(url, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            }).then((response) => {
                if (!response.ok) {
                    if (response.status === 404 || response.status === 500) {
                        // object corresponding to path was not retrievable, resulting in 404 or 500
                        // we cannot do anything about that. ignoring.
                        resolve();
                    }
                    throw new Error(`Unexpected status code: ${response.status}`);
                }
                try {
                    resolve(response.json());
                } catch (error) {
                    reject(error);
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }

    /**
     * Update tree
     *
     * @returns {Promise} - promise for fetching the tree
     */
    updateTree() {
        let currentPath;
        let offset = '/';
        let invisibles = [];
        if (this.active) {
            [this.privateNodesPath, this.privateNodesParent, currentPath, invisibles] = this.#analyzeBreadcrumb();

            // if available use current path of the last breadcrumb node
            // otherwise use the canonical url
            if (typeof currentPath === 'undefined') {
                const linkCanonical = document.querySelector('link[rel="canonical"]');
                const currentUrl = linkCanonical.href;
                currentPath = currentUrl.replace(window.PORTAL_URL, '');
            }

            // main navigation
            if (this.rootPath !== '/') {
                // use rootPath as offset and remove it from currentPath start
                offset = this.rootPath.concat('/');
                currentPath = currentPath.slice(this.rootPath.length + 1);
            } else { // section navigation
                // current path is complete path without leading /
                currentPath = currentPath.slice(1);
            }
        } else if (this.rootPath === '/') {
            // main navi is visible also when not active
            currentPath = '';
        } else {
            // inactive section navi will not be shown at all
            return Promise.resolve();
        }

        // build path segments until the depth of urlSplitCount
        // (for with urlSplitCount=2 it is ['/', '/bu', '/bu/architektur'])
        const pathPieces = currentPath.split('/');
        const requestPieces = [];
        // root path (offset) always needs to be fetched except it is private
        if (!this.privateNodesPath || !this.privateNodesPath.includes(this.rootPath)) {
            requestPieces.push(offset);
        }

        let reqPath = '';
        let counter = 0;
        for (let i = 0; i < pathPieces.length; i += 1) {
            if (reqPath) {
                reqPath = reqPath.concat('/');
            }
            reqPath = reqPath.concat(pathPieces[i]);
            counter += 1;
            // add path only if it isn't a private node as these cannot be requested
            // if its a private node then don't reset the counter and try the next node
            // in combination with private nodes and the number in urlSplitCount, gaps may occur in the requestPieces
            // however, this is compensated by the later data query
            if (!this.privateNodesPath.includes(offset + reqPath)) {
                // add path if it's the first entry or every 'urlSplitCount' times
                if (requestPieces.length < 1 || counter >= this.urlSplitCount) {
                    counter = 0;
                    // build complete path
                    const localPath = offset + reqPath;
                    // remove it from the invisibles array, if it is in there
                    const index = invisibles.indexOf(localPath);
                    if (index > -1) {
                        invisibles.splice(index, 1);
                    }
                    // add path to request pieces array
                    requestPieces.push(localPath);
                }
            }
        }
        // add the invisible items that were created via breadcrumbs (that are not private) so they will get requested
        if (invisibles.length) {
            // the double hits (request pieces that are in invisibles) have been removed already
            requestPieces.push(...invisibles);
        }

        // create a list of Promises; each one for a piece of the path
        const fetchPromises = [];
        requestPieces.forEach((path) => {
            const aPromise = this.getPathData(path);
            fetchPromises.push(aPromise);
        });

        return new Promise((resolve, reject) => {
            Promise.all(
                fetchPromises,
            ).then(() => {
                // compute rootKey, which has the shortest path
                /* Attention:
                Iterator (getKeys result) got the reduce method fairly late,
                so we convert to Array before reducing for compatibility. */
                const rootKey = Array.from(this.pathsMap.getKeys()).reduce((a, b) => (a.length <= b.length ? a : b));
                // create the real root from the rootKey
                this.root = new TreeObject(this, this.pathsMap.getData(rootKey));
                // split for the pieces to traverse the TreeObject tree to find the current object
                const pieces = currentPath.split('/');
                let activeObj = this.root;
                let stop = false;
                let dynamic = false;
                // step down the tree as far as the path corresponds to child objects
                pieces.forEach((segment) => {
                    if (!stop) {
                        // lookup child
                        const sub = activeObj.getChild(segment);
                        if (sub) {
                            // check for dynamic
                            dynamic = dynamic || sub.dynamic;
                            activeObj = sub;
                        } else {
                            stop = true;
                        }
                    }
                });
                // we found a dynamic node on the way, so check for an active-marker in further children
                if (dynamic) {
                    // handle dynamic objects by checking all children
                    const recurse = (nextChild) => {
                        let foundOne = nextChild.active ? nextChild : undefined;
                        nextChild.children.forEach((child) => {
                            // look deeper
                            const other = recurse(child);
                            // when nothing was found, child is the active one
                            if (other) foundOne = other;
                        });
                        return foundOne;
                    };
                    const activeChild = recurse(activeObj);
                    if (activeChild) activeObj = activeChild;
                }
                // mark the object as active (it represents the current page)
                this.activeObject = activeObj;

                resolve();
            }).catch((error) => {
                reject(error);
            });
        });
    }

    /**
     *
     * @param {*} treeObject
     * @returns
     */
    getSyncedObject(treeObject) {
        let tempObject = this.root;
        // We walk down the breadcrumbs only as these are the objects that can be opened by the user
        treeObject.breadcrumbs.slice(1).every((pathObject) => {
            // get the child (with init if neccessary)
            const nextTempObject = tempObject.getChild(pathObject.id);
            if (nextTempObject) {
                tempObject = nextTempObject;
                return true;
            }
            // paths are out of sync here, break
            return false;
        });
        return tempObject;
    }

    /**
     * Synchronous data expansion with TreeObject child creation
     * @param TreeObject treeObject
     */
    expandData(treeObject) {
        const obj = treeObject.jsonObject;
        if (obj.has_children !== false) {
            // this check is for the structure api (and upper levels in the hierarchy for default api)
            if (obj.children.length === 0) {
                // children are not set (we are on the slicing border or invisible)
                // TODO: when adding a path to the pathmap, children should be repaired (up and down)
                // TODO: then this check and the repair become obsolete
                const data = this.getPathDataSynced(
                    // use path to request child data
                    obj.url.replace(window.PORTAL_URL, ''),
                );

                // repair the structure
                if (data?.children) {
                    obj.children = data.children;
                    obj.has_children = data.has_children;
                }
            }
            // create the children when data handling is done
            treeObject.createChildren();
        }
    }

    /**
     * get data from Map synchronously, do not request it from backend
     * @param {*} path
     * @returns
     */
    getPathDataSynced(path) {
        return this.pathsMap.getData(path);
    }

    /**
     * get data from Map if available, otherwise get it from backend
     * @param {string} path
     * @param {boolean} knownDynamic
     * @returns {Promise} - promise for fetching the tree
     */
    getPathData(path, knownDynamic = false) {
        const data = this.pathsMap.getData(path);

        return new Promise((resolve, reject) => {
            // existing data is just returned except
            // 1) it's not fully loaded (no data or should have children) or
            // 2) it is marked for reload (when created from breadcrumb) or
            // 3) knownDynamic is true (e.g. on and below extenders)
            if ((typeof data !== 'undefined')
                && (data.has_children === false || data.children.length)
                && !data.force_reload
                && !knownDynamic) {
                resolve(data);
            } else {
                this.#getSubtreeData(path, knownDynamic).then((subtreeData) => {
                    // no data, no handling, done
                    if (!subtreeData) {
                        // nothing returned -> okay, still resolve so we can ignore that part of the path
                        resolve();
                    }
                    // chunk subtreeData into pieces for pathsMap
                    const dynamicList = [];
                    const privateNodesParentKeys = this.privateNodesParent ? Object.keys(this.privateNodesParent) : [];

                    // store tree data for every entry
                    const recurseCheckinData = (checkinData, recurseCount = 0) => {
                        const localCheckinData = checkinData;
                        // make sure url exists
                        if (!localCheckinData.url) {
                            return;
                        }
                        const checkinPath = localCheckinData.url.slice(window.PORTAL_URL.length);
                        // if this dataset has private children data append it to children
                        // if this dataset is the last node at current path block then do not add private data; it will
                        // be added with the next path block
                        if (privateNodesParentKeys.includes(checkinPath) && recurseCount < this.urlSplitCount) {
                            localCheckinData.children.push(this.privateNodesParent[checkinPath]);
                            // make sure that the data object knows its children
                            localCheckinData.has_children = true;
                        }

                        // TODO: optimize: remove children data from checkinData?
                        this.pathsMap.setData(checkinPath, localCheckinData);
                        // mark data for Re-request when dynamic is set (and we are not handling it currently)
                        if (localCheckinData.dynamic && !knownDynamic) {
                            dynamicList.push(localCheckinData);
                        }
                        // checkin all children with further recursion
                        localCheckinData.children.forEach((child) => {
                            // do not check in data for private children; this has been done before
                            if (!child.is_private) {
                                recurseCheckinData(child, recurseCount + 1);
                            }
                        });
                    };
                    // call recursive method to checkin data
                    recurseCheckinData(subtreeData);

                    // check dynamicData for re-requests
                    if (dynamicList.length) {
                        // only resolve after all re-requests are done
                        Promise.all(
                            dynamicList.map((dynamicData) => {
                                const dynamicPath = dynamicData.url.slice(window.PORTAL_URL.length);
                                // TODO: Use getSubtreeData instead of getPathData?
                                return this.getPathData(dynamicPath, true);
                            }),
                        ).then(() => {
                            // get the new dataset from the map (in case of re-requests)
                            const dataPath = subtreeData.url.slice(window.PORTAL_URL.length);
                            resolve(this.pathsMap.getData(dataPath));
                        }).catch((error) => {
                            reject(error);
                        });
                    } else {
                        resolve(subtreeData);
                    }
                }).catch((error) => {
                    reject(error);
                });
            }
        });
    }

    /**
     * Search stub, not implemented for DefaultApi
     * @param TreeObject treeObject
     * @param {*} str
     * @returns
     */
    search(searchString) {
        return [];
    }
}
